import type { ComponentProps, ComponentType } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import type CarDetailPage from "@/pages/CarDetailPage";
import type LegacyCarPage from "@/pages/LegacyCarPage";
import type LegacyCollectionPage from "@/pages/LegacyCollectionPage";
import type LandingPageCatchAll from "@/pages/LandingPageCatchAll";
import { matchPublicKnowledgePath } from "../../../../lib/copy/src/help-article-path.mjs";
import { loadEagerKnowledgePage } from "./public-knowledge-routes";
import { loadEagerStaticPage, staticPublicPath } from "./public-static-source";
import { loadEagerGeoPage } from "./public-geo-routes";
export { PublicCityGuideRoute, PublicModelGuideRoute, PublicUseCaseGuideRoute,
  PublicComparisonGuideRoute, PublicHireLandingRoute, PublicModelsIndexRoute,
  PublicCitiesIndexRoute, PublicUseCasesIndexRoute, PublicComparisonsIndexRoute } from "./public-geo-routes";

const LazyCar = lazyRetry(() => import("@/pages/CarDetailPage"));
const LazyCarSlugRedirect = lazyRetry(() => import("@/pages/CarSlugRedirect"));
const LazyLegacyCar = lazyRetry(() => import("@/pages/LegacyCarPage"));
const LazyCollection = lazyRetry(() => import("@/pages/LegacyCollectionPage"));
const LazySearch = lazyRetry(() => import("@/pages/SearchPage"));
const LazyNotFound = lazyRetry(() => import("@/pages/not-found"));
let notFound: typeof import("@/pages/not-found").default | undefined;
export function NotFoundRoute() {
  const Page = notFound || LazyNotFound;
  return <Page />;
}
export function loadEagerNotFoundPage(): Promise<void> {
  return loadDefinition("not-found", async () => {
    notFound = (await import("@/pages/not-found")).default;
  });
}
let search: typeof import("@/pages/SearchPage").default | undefined;
const definitions = new Map<string, Promise<void>>();
function loadDefinition(name: string, load: () => Promise<void>) {
  if (!definitions.has(name)) definitions.set(name, load().catch(error => {
    definitions.delete(name); throw error;
  }));
  return definitions.get(name)!;
}
let car: typeof CarDetailPage | undefined;
let legacyCar: typeof LegacyCarPage | undefined;
let collection: typeof LegacyCollectionPage | undefined;
let landing: typeof LandingPageCatchAll | undefined;
type LandingRouteProps = ComponentProps<typeof LandingPageCatchAll> & { params?: object };
export function eagerLandingPage(fallback: ComponentType<LandingRouteProps>): ComponentType<LandingRouteProps> {
  return landing || fallback;
}
let loading: Promise<void> | undefined;

/** Module definitions only; never request data, auth state or a query client. */
export function loadEagerPublicPages(pathname?: string): Promise<void> {
  if (!pathname && typeof window !== "undefined") {
    const base = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "");
    const path = window.location.pathname;
    pathname = base && (path === base || path.startsWith(base + "/")) ? path.slice(base.length) || "/" : path;
  }
  const knowledge = pathname ? matchPublicKnowledgePath(pathname) : null;
  if (knowledge) return loadEagerKnowledgePage(knowledge.type);
  if (pathname && /\/(?:search|cars)\/?$/.test(pathname))
    return loadDefinition("search", async () => { search = (await import("@/pages/SearchPage")).default; });
  const geo = pathname ? loadEagerGeoPage(pathname) : undefined;
  if (geo) return geo;
  const staticPath = pathname ? staticPublicPath(pathname) : null;
  if (staticPath) return loadEagerStaticPage(staticPath);
  // Hydrate only this route's definition. Loading all four also downloaded the
  // landing/Help source graphs on every car and collection page.
  if (pathname) {
    if (/\/collection\/[^/]+\/?$/.test(pathname))
      return loadDefinition("collection", async () => { collection = (await import("@/pages/LegacyCollectionPage")).default; });
    if (/\/car\/[^/]+(?:\/(?:home|search))?\/?$/.test(pathname))
      return loadDefinition("legacy-car", async () => { legacyCar = (await import("@/pages/LegacyCarPage")).default; });
    if (/\/cars\/[^/]+(?:\/[^/]+)?\/?$/.test(pathname))
      return loadDefinition("car", async () => { car = (await import("@/pages/CarDetailPage")).default; });
    return loadDefinition("landing", async () => { landing = (await import("@/pages/LandingPageCatchAll")).default; });
  }
  if (!loading) loading = Promise.all([
    import("@/pages/CarDetailPage"),
    import("@/pages/LegacyCarPage"),
    import("@/pages/LegacyCollectionPage"),
    import("@/pages/LandingPageCatchAll"),
  ]).then(([carModule, legacyModule, collectionModule, landingModule]) => {
    car = carModule.default;
    legacyCar = legacyModule.default;
    collection = collectionModule.default;
    landing = landingModule.default;
  }).catch(error => {
    loading = undefined;
    throw error;
  });
  return loading;
}

type RouteParams = { params?: Record<string, string | undefined> };
/** A combined public canonical is a real detail page, not an ID-only alias. */
export function PublicCarIdentityRoute({ params }: RouteParams = {}) {
  const combined = /^([a-z0-9][a-z0-9-]*)-([1-9]\d*)$/.exec(params?.id || "");
  return combined
    ? <PublicCarRoute resolvedCarId={combined[2]} resolvedSlug={combined[1]} />
    : <LazyCarSlugRedirect />;
}
export function PublicSearchRoute() { const Page = search || LazySearch; return <Page />; }
export function PublicCarRoute(props: NonNullable<ComponentProps<typeof CarDetailPage>> & RouteParams = {}) {
  const Page = car || LazyCar;
  return <Page {...props} />;
}
export function PublicLegacyCarRoute() {
  const Page = legacyCar || LazyLegacyCar;
  return <Page />;
}
export function PublicCollectionRoute(props: NonNullable<ComponentProps<typeof LegacyCollectionPage>> & RouteParams = {}) {
  const Page = collection || LazyCollection;
  return <Page {...props} />;
}
